import { useTranslation } from 'react-i18next';
import { ActivityLogFilterBar } from '@/components/common/ActivityLog/ActivityLogFilterBar';
import { ActivityLogResults } from '@/components/common/ActivityLog/ActivityLogResults';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useActivityLogFilters } from '@/hooks/useActivityLogFilters';
import { useClientQuery } from '@/hooks/useClientQuery';
import { useWorkspaceAuditLogsQuery } from '@/hooks/useWorkspaceAuditLogsQuery';

interface WorkspaceActivitySectionProps {
  clientId: string | null;
  onClearClient: () => void;
}

export function WorkspaceActivitySection({
  clientId,
  onClearClient,
}: WorkspaceActivitySectionProps) {
  const { t } = useTranslation('activity');
  const filters = useActivityLogFilters(clientId);
  const entriesQuery = useWorkspaceAuditLogsQuery(filters.query, true);

  return (
    <div>
      <p className="text-[13px] text-zoomuted mb-4 max-w-3xl">{t('workspaceIntro')}</p>
      {clientId === null ? null : (
        <ClientFilterNotice clientId={clientId} onClearClient={onClearClient} />
      )}
      <ActivityLogFilterBar filters={filters} idPrefix="workspace-activity" />
      <ActivityLogResults
        entriesQuery={entriesQuery}
        hasActiveFilters={filters.hasActiveFilters || clientId !== null}
        showWorkspace={false}
        page={filters.page}
        pageSize={filters.query.pageSize}
        onPageChange={filters.setPage}
      />
    </div>
  );
}

interface ClientFilterNoticeProps {
  clientId: string;
  onClearClient: () => void;
}

function ClientFilterNotice({ clientId, onClearClient }: ClientFilterNoticeProps) {
  const { t } = useTranslation('activity');
  const clientQuery = useClientQuery(clientId);
  const clientName = clientQuery.data === undefined ? '' : clientQuery.data.fullName;

  return (
    <Card
      padding="sm"
      shadow={false}
      className="mb-4 flex items-center justify-between gap-3 flex-wrap"
    >
      <span className="inline-flex items-center gap-2 text-[13px] text-zooindigo min-w-0">
        <Icon name="Filter" />
        <span className="truncate">
          {clientName.length === 0
            ? t('clientFilter.loading')
            : t('clientFilter.showing', { name: clientName })}
        </span>
      </span>
      <Button variant="outline" size="chip" onClick={onClearClient}>
        {t('clientFilter.showEveryone')}
      </Button>
    </Card>
  );
}
